import { emptyProgress, type LearnerProgress } from "../types";
import { normalizePlan } from "./hifz-plan";

const STORAGE_KEY = "al-noor-quran-journey-v2";

export function readLocalProgress(): LearnerProgress {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyProgress();
    return normalizeProgress(JSON.parse(raw));
  } catch {
    return emptyProgress();
  }
}

export function writeLocalProgress(progress: LearnerProgress) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
}

/** The device's own calendar day, "YYYY-MM-DD". */
export function localDay(date = new Date()): string {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

/**
 * Points are what the learner has DONE, not a running total.
 *
 * They used to be added on every mark and never taken away, so unmarking and
 * re-marking an ayah paid out again. Deriving them makes that impossible.
 */
export function learnerPoints(progress: Pick<LearnerProgress, "completedQaida" | "completedAyahs">): number {
  return progress.completedQaida.length * 50 + progress.completedAyahs.length * 5;
}

/**
 * Days in a row, ending today, on which the learner practised.
 *
 * The streak used to be a stored number that started at 1 and was never
 * recalculated, so every learner saw "1 day streak" for ever. Today not being
 * done yet does not break a real streak: an empty today is skipped once.
 */
export function streakFrom(days: string[], now = new Date()): number {
  const practised = new Set(days);
  const cursor = new Date(now.getTime());
  let streak = 0;
  for (let step = 0; step < 400; step += 1) {
    if (practised.has(localDay(cursor))) streak += 1;
    else if (step > 0) break;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

/**
 * Ayahs practised this week (Monday to today), for the weekly goal.
 *
 * The goal used to compare against every ayah ever practised, so it filled once
 * and stayed full. Monday is when a Pakistani school week starts again.
 */
export function weekPractice(log: Record<string, number>, now = new Date()): number {
  const today = localDay(now);
  const monday = new Date(now.getTime());
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  const from = localDay(monday);
  return Object.entries(log).reduce((sum, [day, count]) => (day >= from && day <= today ? sum + count : sum), 0);
}

/** Record that the learner practised today. */
export function stampActivity(progress: LearnerProgress, now = new Date()): LearnerProgress {
  const today = localDay(now);
  const activeDays = progress.activeDays.includes(today) ? progress.activeDays : [today, ...progress.activeDays].slice(0, 400);
  return { ...progress, activeDays, lastActive: now.toISOString(), points: learnerPoints(progress) };
}

/** Count (or, when unmarking, uncount) one practised ayah against today. */
export function logPractice(log: Record<string, number>, delta: 1 | -1, now = new Date()): Record<string, number> {
  const today = localDay(now);
  const next = Math.max(0, (log[today] ?? 0) + delta);
  const updated = { ...log, [today]: next };
  if (!next) delete updated[today];
  return trimLog(updated);
}

export function normalizeProgress(value: Partial<LearnerProgress> | null | undefined): LearnerProgress {
  const fallback = emptyProgress();
  const completedQaida = stringArray(value?.completedQaida);
  const completedAyahs = stringArray(value?.completedAyahs);
  return {
    completedQaida,
    completedAyahs,
    bookmarks: stringArray(value?.bookmarks),
    points: learnerPoints({ completedQaida, completedAyahs }),
    streak: positiveNumber(value?.streak, fallback.streak),
    lastActive: typeof value?.lastActive === "string" ? value.lastActive : fallback.lastActive,
    lastRead: value?.lastRead && Number.isInteger(value.lastRead.surah) && Number.isInteger(value.lastRead.ayah)
      ? { surah: value.lastRead.surah, ayah: value.lastRead.ayah }
      : undefined,
    weeklyGoal: positiveNumber(value?.weeklyGoal, fallback.weeklyGoal),
    classCovered: stringArray(value?.classCovered),
    sharedResources: sharedList(value?.sharedResources),
    activeDays: dayList(value?.activeDays),
    practiceLog: practiceMap(value?.practiceLog),
    hifzPlan: normalizePlan(value?.hifzPlan, localDay()),
  };
}

/**
 * Combine the device's journey with the one held in the cloud.
 *
 * Both sides are normalised first. They are supposed to be already, but this
 * function is where a missing array becomes a thrown TypeError inside a React
 * state updater — which unmounts the whole app rather than degrading — so it
 * defends itself instead of trusting its callers.
 *
 * Sets are UNIONED, never replaced by the "newer" side: a new device starts
 * with an empty journey stamped with today's date, and letting that win would
 * wipe a learner's record the first time they signed in on a second phone.
 */
export function mergeProgress(localInput: LearnerProgress, remoteInput: LearnerProgress): LearnerProgress {
  const local = normalizeProgress(localInput);
  const remote = normalizeProgress(remoteInput);
  const completedQaida = unique([...local.completedQaida, ...remote.completedQaida]);
  const completedAyahs = unique([...local.completedAyahs, ...remote.completedAyahs]);
  const practiceLog: Record<string, number> = { ...remote.practiceLog };
  for (const [day, count] of Object.entries(local.practiceLog)) practiceLog[day] = Math.max(practiceLog[day] ?? 0, count);
  return {
    completedQaida,
    completedAyahs,
    bookmarks: unique([...local.bookmarks, ...remote.bookmarks]),
    points: learnerPoints({ completedQaida, completedAyahs }),
    streak: Math.max(local.streak, remote.streak),
    lastActive: local.lastActive > remote.lastActive ? local.lastActive : remote.lastActive,
    lastRead: local.lastActive > remote.lastActive ? local.lastRead : remote.lastRead,
    weeklyGoal: remote.weeklyGoal || local.weeklyGoal,
    classCovered: unique([...local.classCovered, ...remote.classCovered]),
    // Union by key, newest wins: a teacher who shares the same book in two
    // lessons should not leave the learner with two entries for it.
    sharedResources: Object.values(
      [...local.sharedResources, ...remote.sharedResources]
        .reduce<Record<string, LearnerProgress["sharedResources"][number]>>((all, item) => {
          const existing = all[item.key];
          if (!existing || existing.sharedAt < item.sharedAt) all[item.key] = item;
          return all;
        }, {}),
    ).sort((a, b) => b.sharedAt.localeCompare(a.sharedAt)),
    activeDays: dayList([...local.activeDays, ...remote.activeDays]),
    practiceLog: trimLog(practiceLog),
    // The plan most recently changed wins, on whichever device that was.
    hifzPlan: !local.hifzPlan ? remote.hifzPlan
      : !remote.hifzPlan ? local.hifzPlan
        : local.hifzPlan.updatedAt >= remote.hifzPlan.updatedAt ? local.hifzPlan : remote.hifzPlan,
  };
}

const unique = (values: string[]) => [...new Set(values)];
const stringArray = (value: unknown) => Array.isArray(value) ? unique(value.filter((item): item is string => typeof item === "string")) : [];
// Character classes rather than \d: a backslash in a pattern in this repo has
// been silently eaten by tooling before.
const DAY = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;
const dayList = (value: unknown) => Array.isArray(value)
  ? unique(value.filter((item): item is string => typeof item === "string" && DAY.test(item))).sort().reverse().slice(0, 400)
  : [];
const trimLog = (log: Record<string, number>) => Object.fromEntries(
  Object.entries(log).sort((a, b) => b[0].localeCompare(a[0])).slice(0, 60),
);
const practiceMap = (value: unknown): Record<string, number> => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return trimLog(Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .filter((entry): entry is [string, number] => DAY.test(entry[0]) && typeof entry[1] === "number" && Number.isFinite(entry[1]) && entry[1] > 0)
      .map(([day, count]) => [day, Math.min(10_000, Math.floor(count))]),
  ));
};
// `/` and `.` as one-character classes rather than escapes: a backslash in this
// pattern has been eaten by tooling before, and a key check that silently stops
// matching would let anything through.
const SYLLABUS_KEY = /^syllabus[/][a-f0-9]{8,32}[.][a-z0-9]{2,4}$/i;

const sharedList = (value: unknown): LearnerProgress["sharedResources"] => Array.isArray(value)
  ? value
    .filter((item): item is LearnerProgress["sharedResources"][number] =>
      Boolean(item) && typeof item === "object"
      && typeof (item as { key?: unknown }).key === "string"
      && SYLLABUS_KEY.test((item as { key: string }).key))
    .slice(0, 200)
  : [];
const positiveNumber = (value: unknown, fallback: number) => typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : fallback;
